// ---------------------------------------------------------------------------
// knowledge/index.ts — aggregated knowledge corpus + tenant overlay seam
//
// ALL_UNITS is the single export consumed by:
//   - D4 (inMemoryKnowledge provider, system-prompt assembler)
//   - E1 (useAdvisor provider singleton)
//   - index.test.ts (structural validation)
//
// The corpus is CORE_UNITS (tenant-agnostic base) plus an optional per-tenant
// overlay selected at build time by VITE_TENANT_KEY (config/tenant.ts →
// TENANT_KEY). A build that names a tenant with no registered overlay throws at
// module init — no silent fallback to the core corpus (project rule).
//
// Draft revision (not yet analyst-greenlit per spec §18).
// ---------------------------------------------------------------------------

export type { KnowledgeUnit } from '../types'

export { pages } from './pages'
export { routing } from './routing'
export { kpis } from './kpis'
export { concepts } from './concepts'

import { pages } from './pages'
import { routing } from './routing'
import { kpis } from './kpis'
import { concepts } from './concepts'
import { GREENCRATE_OVERLAY } from './tenants/greencrate'
import type { KnowledgeUnit } from '../types'
import { TENANT_KEY } from '../../../config/tenant'

/** Tenant-agnostic base corpus — present in every build. */
export const CORE_UNITS: KnowledgeUnit[] = [...pages, ...routing, ...kpis, ...concepts]

/** A tenant's corpus overlay: extra knowledge units + welcome-block starter questions. */
export interface TenantOverlay {
  units: KnowledgeUnit[]
  /** Clickable starter questions for the advisor welcome block. Empty → core set is used. */
  exampleQuestions: string[]
}

/** Tenant-agnostic starter questions — used when no tenant overlay provides its own. */
export const CORE_EXAMPLE_QUESTIONS: string[] = [
  'What can I see on this page?',
  'Which medium delivered the best RoI?',
  'How is contribution attributed to media?',
]

/** Per-tenant overlays, selected at build time by VITE_TENANT_KEY. */
export const TENANT_OVERLAYS: Record<string, TenantOverlay> = {
  greencrate: GREENCRATE_OVERLAY,
}

/**
 * Resolve the active corpus + example questions from a tenant key.
 *   null        → core only (Vodafone/prod-t1 builds set no key)
 *   known key   → units appended to core; overlay questions REPLACE core questions
 *                 (falling back to core when the overlay provides none)
 *   unknown key → THROW (loud). A build naming a tenant we ship no corpus for is
 *                 a configuration error, not a reason to serve the core corpus.
 *
 * Pure and side-effect free (dependencies injected) so it can be exercised with
 * fixture overlays independent of which real tenant files are present.
 */
export function resolveOverlay(
  tenantKey: string | null,
  core: TenantOverlay,
  overlays: Record<string, TenantOverlay>,
): TenantOverlay {
  if (tenantKey === null) return core
  const overlay = overlays[tenantKey]
  if (overlay === undefined) {
    const known = Object.keys(overlays).sort().join(', ') || '(none)'
    throw new Error(
      `VITE_TENANT_KEY="${tenantKey}" has no registered advisor corpus overlay (known: ${known})`,
    )
  }
  return {
    units: [...core.units, ...overlay.units],
    exampleQuestions:
      overlay.exampleQuestions.length > 0 ? overlay.exampleQuestions : core.exampleQuestions,
  }
}

const RESOLVED = resolveOverlay(
  TENANT_KEY,
  { units: CORE_UNITS, exampleQuestions: CORE_EXAMPLE_QUESTIONS },
  TENANT_OVERLAYS,
)

export const ALL_UNITS: KnowledgeUnit[] = RESOLVED.units
export const EXAMPLE_QUESTIONS: string[] = RESOLVED.exampleQuestions
